import React, { useState, useEffect } from "react";
import axios from "axios";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

const API_URL = process.env.REACT_APP_API_URL;

const NEW_YEAR_OPTION = "new";
const EMPTY_FORM = { year: "", single: "", family: "" };

// FastAPI returns a string detail for our own errors but a list for schema
// validation failures, so only strings are safe to show.
const detailMessage = (err, fallback) => {
    const detail = err.response?.data?.detail;
    return typeof detail === "string" ? detail : fallback;
};

const EditNationalBenchmark = ({ benchmark, setBenchmark }) => {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [benchmarks, setBenchmarks] = useState({});
    const [selectedYear, setSelectedYear] = useState(NEW_YEAR_OPTION);
    const [isEditing, setIsEditing] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const years = Object.keys(benchmarks).sort((a, b) => Number(b) - Number(a));
    const hasBenchmarks = years.length > 0;
    const priorYear = String(new Date().getFullYear() - 1);
    // No values at all blocks the reports; a missing prior year is only a warning.
    const isMissingPriorYear = hasBenchmarks && !years.includes(priorYear);
    const needsAttention = !loading && (!hasBenchmarks || isMissingPriorYear);
    const isNew = selectedYear === NEW_YEAR_OPTION;

    // Applies a server response and re-selects the year it points at.
    const applyState = (data, preferredYear) => {
        const nextBenchmarks = data.benchmarks || {};
        const nextYear =
            preferredYear && nextBenchmarks[preferredYear]
                ? preferredYear
                : data.default_year || NEW_YEAR_OPTION;

        setBenchmarks(nextBenchmarks);
        selectYear(nextYear, nextBenchmarks);
    };

    // Moves the selection without touching the server.
    const selectYear = (year, source) => {
        const available = source || benchmarks;
        setIsEditing(false);
        setError("");

        if (year === NEW_YEAR_OPTION || !available[year]) {
            setSelectedYear(NEW_YEAR_OPTION);
            setForm(EMPTY_FORM);
            setBenchmark(null);
            return;
        }

        setSelectedYear(year);
        setForm({
            year,
            single: String(available[year].single),
            family: String(available[year].family),
        });
        setBenchmark({ year, ...available[year] });
    };

    useEffect(() => {
        let cancelled = false;

        const loadBenchmarks = async () => {
            try {
                const res = await axios.get(`${API_URL}/benchmarks`);
                if (cancelled) return;
                applyState(res.data);
                const empty = Object.keys(res.data.benchmarks || {}).length === 0;
                const missingPrior =
                    !empty && !Object.keys(res.data.benchmarks).includes(String(new Date().getFullYear() - 1));
                setIsCollapsed(!empty && !missingPrior);
            } catch (err) {
                if (cancelled) return;
                setError(
                    detailMessage(err, "Failed to load the National Benchmark Average values. Please try again.")
                );
                setIsCollapsed(false);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadBenchmarks();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleFieldChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleEdit = () => {
        setError("");
        setIsEditing(true);
    };

    const handleCancel = () => {
        selectYear(selectedYear);
    };

    const handleSave = async () => {
        const year = form.year.trim();
        const single = parseFloat(form.single);
        const family = parseFloat(form.family);

        if (!/^\d{4}$/.test(year)) {
            setError("Please enter a valid 4 digit year.");
            return;
        }
        if (isNew && benchmarks[year]) {
            setError(`The year ${year} already exists. Select it to edit its values.`);
            return;
        }
        if (!(single > 0) || !(family > 0)) {
            setError("Please enter single and family annual premiums greater than zero.");
            return;
        }

        try {
            setSaving(true);
            setError("");
            const res = await axios.put(`${API_URL}/benchmarks/${year}`, { single, family });
            applyState(res.data, year);
        } catch (err) {
            setError(
                detailMessage(err, "Failed to save the National Benchmark Average values. Please try again.")
            );
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (isNew || years.length <= 1) return;

        try {
            setSaving(true);
            setError("");
            const res = await axios.delete(`${API_URL}/benchmarks/${selectedYear}`);
            applyState(res.data);
        } catch (err) {
            setError(
                detailMessage(err, "Failed to delete the National Benchmark Average values. Please try again.")
            );
        } finally {
            setSaving(false);
        }
    };

    const inputsLocked = !isNew && !isEditing;
    const showSaveCancel = isNew || isEditing;
    // In "New" there is nothing to go back to, so Cancel stays blocked and the
    // year dropdown is the way out.
    const cancelDisabled = isNew || saving;

    const inputClass = (locked) =>
        `w-full pl-8 pr-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${locked ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""
        }`;

    return (
        <div
            className={`bg-white p-6 rounded-lg shadow-lg border mb-6 ${needsAttention ? "border-yellow-400 ring-2 ring-yellow-300" : "border-gray-200"
                }`}
        >
            <div className={`flex justify-between items-center ${isCollapsed ? "mb-0" : "mb-4"}`}>
                <div className="flex items-center space-x-3">
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className="text-blue-600 hover:text-blue-800 focus:outline-none"
                    >
                        <span className="text-xl font-bold">
                            {isCollapsed ? (
                                <ChevronUpIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                            ) : (
                                <ChevronDownIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                            )}
                        </span>
                    </button>
                    <h2 className="text-xl font-bold text-gray-800">
                        National Benchmark Average values
                    </h2>
                </div>

                <div className="flex space-x-2">
                    <select
                        className="px-3 border border-gray-300 rounded text-gray-700 bg-white hover:border-gray-400 focus:outline-none focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        value={selectedYear}
                        disabled={loading || saving || isEditing}
                        onChange={(e) => selectYear(e.target.value)}
                    >
                        <option value={NEW_YEAR_OPTION}>New</option>
                        {years.map((year) => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </select>

                    {showSaveCancel ? (
                        <>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className={`px-4 py-2 rounded text-white font-medium transition-colors ${saving ? "bg-gray-400 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                                    }`}
                            >
                                {saving ? "Saving..." : "Save"}
                            </button>
                            <button
                                onClick={handleCancel}
                                disabled={cancelDisabled}
                                className={`px-4 py-2 rounded font-medium transition-colors ${cancelDisabled
                                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                    : "bg-gray-500 text-white hover:bg-gray-600"
                                    }`}
                            >
                                Cancel
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={handleEdit}
                                disabled={saving}
                                className="px-4 py-2 rounded text-white font-medium bg-blue-500 hover:bg-blue-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                Edit
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={saving || years.length <= 1}
                                title={years.length <= 1 ? "The last remaining year cannot be deleted" : ""}
                                className={`px-4 py-2 rounded font-medium transition-colors ${saving || years.length <= 1
                                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                    : "bg-red-500 text-white hover:bg-red-600"
                                    }`}
                            >
                                Delete
                            </button>
                        </>
                    )}
                </div>
            </div>

            {!isCollapsed && (
                <>
                    {loading ? (
                        <p className="text-gray-500">Loading values...</p>
                    ) : (
                        <>
                            {!hasBenchmarks && (
                                <div className="mb-4 p-2 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 text-sm">
                                    There are no National Benchmark Average values yet. Add a year before
                                    generating a report.
                                </div>
                            )}
                            {isMissingPriorYear && (
                                <div className="mb-4 p-2 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 text-sm">
                                    There are no values for {priorYear} yet. Reports will use{" "}
                                    {selectedYear === NEW_YEAR_OPTION ? "the latest year available" : selectedYear}{" "}
                                    unless you add them.
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="border border-gray-300 rounded-lg p-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            min="1900"
                                            max="2999"
                                            step="1"
                                            value={form.year}
                                            disabled={!isNew}
                                            onChange={(e) => handleFieldChange("year", e.target.value)}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isNew ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""
                                                }`}
                                            placeholder="YYYY"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {isNew ? "Year these values belong to" : "The year cannot be changed"}
                                    </p>
                                </div>

                                <div className="border border-gray-300 rounded-lg p-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Single Annual Premium
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                            $
                                        </span>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={form.single}
                                            disabled={inputsLocked}
                                            onChange={(e) => handleFieldChange("single", e.target.value)}
                                            className={inputClass(inputsLocked)}
                                            placeholder="0.00"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">National average (KFF.org)</p>
                                </div>

                                <div className="border border-gray-300 rounded-lg p-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Family Annual Premium
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                            $
                                        </span>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={form.family}
                                            disabled={inputsLocked}
                                            onChange={(e) => handleFieldChange("family", e.target.value)}
                                            className={inputClass(inputsLocked)}
                                            placeholder="0.00"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">National average (KFF.org)</p>
                                </div>
                            </div>

                            {benchmark && (
                                <p className="text-sm text-gray-500 mt-4">
                                    Reports will use the {benchmark.year} values.
                                </p>
                            )}

                            {error && <div className="text-red-600 mt-4">{error}</div>}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default EditNationalBenchmark;
